import { useEffect, useState } from 'react';
import { browserAdapter } from '../lib/adapters/browser';

/**
 * /auth/accept — PWA side of the platform SSO handshake.
 *
 * Entry: platform.khal.ai/v1/auth/callback redirects here with
 *   #kernel=<encoded-url>&token=<jwt>
 *
 * Flow:
 *   1. Parse the fragment (never goes to the server, keeps JWT out of logs).
 *   2. POST the token to `${kernel}/api/auth/accept` for server-side
 *      validation (ed25519 signature check + email-domain gate). We could
 *      skip this and store-on-faith, but then a forged JWT would look
 *      fine to the PWA; belt-and-braces is cheap here.
 *   3. On 204, persist { serverUrl, wsUrl, token } via browserAdapter.
 *      wsUrl derivation matches the kernel path (`/ws/nats`).
 *   4. Redirect to `/` so main.tsx re-mounts with isEnterprise=true.
 *
 * On any failure, render a clear error — never flash the shell.
 */

type Status = 'loading' | 'error';
interface AcceptError {
	kind: string;
	email?: string;
}

function parseFragment(hash: string): { kernel: string | null; token: string | null } {
	const h = hash.startsWith('#') ? hash.slice(1) : hash;
	const params = new URLSearchParams(h);
	return {
		kernel: params.get('kernel'),
		token: params.get('token'),
	};
}

/**
 * Decode JWT payload (base64url). Returns null if malformed.
 * Not verifying here — that's the kernel's job in /api/auth/accept.
 * We're only introspecting the claims to catch stale tokens before we
 * persist them and let the UserMenu fall through to raw userId.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
	try {
		const [, payloadB64] = token.split('.');
		if (!payloadB64) return null;
		const b64 = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
		const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
		return JSON.parse(atob(padded)) as Record<string, unknown>;
	} catch {
		return null;
	}
}

/**
 * Reject tokens minted before the G-USER profile rollout (pre-2026-04-17).
 * A valid post-rollout platform JWT MUST carry `email`; its absence means
 * the issuer predates our claim expansion and the UserMenu would render a
 * raw user_01XXX with no avatar. Fail-loud so the user gets re-authed
 * instead of a broken identity.
 */
function isStaleToken(token: string): boolean {
	const payload = decodeJwtPayload(token);
	if (!payload) return true;
	const email = payload.email;
	return typeof email !== 'string' || email.length === 0;
}

export function AuthAcceptScreen() {
	const [status, setStatus] = useState<Status>('loading');
	const [error, setError] = useState<AcceptError | null>(null);

	useEffect(() => {
		(async () => {
			const { kernel, token } = parseFragment(window.location.hash);
			if (!kernel || !token) {
				setStatus('error');
				setError({ kind: 'missing_params' });
				return;
			}

			try {
				const res = await fetch(`${kernel}/api/auth/accept`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ token }),
				});

				if (res.status === 204) {
					// Server validated signature + expiry; still guard against a
					// stale claim schema (pre-G-USER tokens have no email claim
					// and would render the UserMenu as raw user_01XXX).
					if (isStaleToken(token)) {
						setStatus('error');
						setError({ kind: 'stale-token' });
						return;
					}
					await browserAdapter.setEnterpriseConfig(kernel, token);
					// Drop the fragment so the JWT doesn't linger in history.
					window.history.replaceState(null, '', '/');
					window.location.replace('/');
					return;
				}

				let body: AcceptError = { kind: `http_${res.status}` };
				try {
					body = (await res.json()) as AcceptError;
				} catch {}
				setStatus('error');
				setError(body);
			} catch {
				setStatus('error');
				setError({ kind: 'network_error' });
			}
		})();
	}, []);

	return (
		<div
			style={{
				position: 'fixed',
				inset: 0,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				background: '#0a0a0a',
				color: '#e0e0e0',
				fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
				padding: '2rem',
				textAlign: 'center',
			}}
		>
			{status === 'loading' ? (
				<p>Signing you in…</p>
			) : (
				<div>
					<h1 style={{ fontSize: '1.3rem', margin: '0 0 0.75rem 0' }}>Sign-in failed</h1>
					<p style={{ color: '#888', margin: 0, maxWidth: '440px', lineHeight: 1.5 }}>
						{error?.kind === 'email-domain' && error?.email ? (
							<>
								Your account (<b>{error.email}</b>) is not permitted on this workspace. Contact your
								administrator to request access.
							</>
						) : error?.kind === 'email-domain' ? (
							<>Your email domain is not permitted on this workspace.</>
						) : error?.kind === 'token-expired' ? (
							<>Your sign-in link has expired. Please try again.</>
						) : error?.kind === 'kernel-not-in-claim' ? (
							<>Your access token was not issued for this workspace.</>
						) : error?.kind === 'missing_params' ? (
							<>Missing kernel or token in the sign-in link.</>
						) : error?.kind === 'network_error' ? (
							<>Could not reach the kernel to verify your sign-in. Retry in a moment.</>
						) : error?.kind === 'stale-token' ? (
							<>Your sign-in token is outdated. Please sign in again.</>
						) : (
							<>Sign-in could not be verified ({error?.kind ?? 'unknown'}).</>
						)}
					</p>
					<p style={{ marginTop: '1.5rem' }}>
						<a href={error?.kind === 'stale-token' ? '/auth/reset' : '/'} style={{ color: '#3b82f6' }}>
							{error?.kind === 'stale-token' ? 'Sign in again' : 'Back to sign-in'}
						</a>
					</p>
				</div>
			)}
		</div>
	);
}
